import React from 'react';

import { formatMessage } from '@99designs/common/i18n';
import { claimBrief } from '__generated__/claimBrief';
import { launchBrief } from '__generated__/launchBrief';
import {
    DesignerSource,
    StructuredBriefErrorReason,
    StructuredBriefFieldsInput,
} from '__generated__/types';
import { updateBrief, updateBriefVariables } from '__generated__/updateBrief';
import { ValidationError } from '__generated__/ValidationError';
import { ApolloClient, ApolloError } from 'apollo-client';
import ErrorBar from 'common/components/ErrorBar';
import Bugsnag from 'common/utils/Bugsnag';
import { Source } from 'launch/consts/signupSource';
import { AuthFormProps, AuthProviderLoginFn } from 'projects/components/AuthContext';
import BriefContext from 'projects/components/BriefContext';
import BriefFooter, { Props as BriefFooterProps } from 'projects/components/BriefFooter';
import { LaunchState } from 'projects/components/LaunchModal';
import { FormErrors, ObservableForm } from 'projects/components/ObservableForm';
import Page from 'projects/components/Page';
import { Props as StepFooterProps } from 'projects/components/StepFooter';
import { MAX_TITLE_LENGTH } from 'projects/consts';
import { trackClickedNextButtonEvent } from 'projects/events';
import { StepState } from 'projects/util/step';
import { redirectLaunchedBrief } from '.';
import queries from './Brief.graphql';
import { history } from './BrowserRouter';

export interface Props<T> {
    schema: keyof StructuredBriefFieldsInput;
    step: StepState;
    brief: BriefFooterProps['brief'] & {
        id: string;
        userId: number;
        fields: T;
        designersWithSource: { source: DesignerSource }[];
    };
    viewer: {
        user: {
            id: number;
        } | null;
    };
    client: ApolloClient<any>;
    login: AuthProviderLoginFn;
    recommendedAuthModal?: AuthFormProps;
    directAuthModal?: AuthFormProps;
    children: (form: ObservableForm<T>) => React.ReactNode;
    refetch: (options: { id: string }) => Promise<any>;
}

interface State<T> {
    saveInProgress: boolean;
    nextInProgress: boolean;
    launchState?: LaunchState;
    error?: ApolloError | Error;
}

class Brief<T> extends React.Component<Props<T>, State<T>> {
    private form: ObservableForm<T>;

    resetStore = async () => {
        await this.props.client.resetStore();
    };

    private defaultAuthModalRecommended: AuthFormProps = {
        source: Source.PROJECTS_LAUNCH,
        signupHeading: __("You're almost there..."),
        signupMessage: <p>{__('Sign up to see your designer recommendations.')}</p>,
        postLogin: this.resetStore,
    };

    private defaultAuthModalDirect: AuthFormProps = {
        source: Source.PROJECTS_LAUNCH,
        signupHeading: __("You're almost there..."),
        signupMessage: <p>{__('Sign up to kick off your project.')}</p>,
    };

    constructor(props: Props<T>) {
        super(props);

        if (props.brief.projectIds.length > 0) {
            redirectLaunchedBrief(props.brief);
        }

        this.form = new ObservableForm<T>(props.brief.fields, {
            onSave: this.save,
            autosaveDelay: 1000,
        });

        this.state = {
            saveInProgress: false,
            nextInProgress: false,
        };
    }

    UNSAFE_componentWillReceiveProps(props: Props<T>) {
        if (this.props.step.number !== props.step.number) {
            window.scrollTo(0, 0);
        }
    }

    clearError() {
        this.setState({ error: undefined });
    }

    async claimIfUnclaimed() {
        if (!this.props.brief.userId) {
            await this.props.client.mutate<claimBrief>({
                mutation: queries.claimBrief,
                variables: { id: this.props.brief.id },
            });
        }
    }

    userInitiatedSave = async () => {
        await this.claimIfUnclaimed();
        try {
            await this.form.save();
        } catch (error) {
            this.setState({ error });
        }

        this.scrollToFirstError();
    };

    handleNextClick = async () => {
        this.setState({
            nextInProgress: true,
        });
        try {
            trackClickedNextButtonEvent({ id: this.props.brief.id });
            try {
                await this.form.save();
                await this.props.refetch({ id: this.props.brief.id });
            } catch (error) {
                this.setState({ error });
            }

            if (this.form.hasErrors()) {
                this.scrollToFirstError();
                return;
            }

            const {
                step,
                viewer: { user },
                recommendedAuthModal = this.defaultAuthModalRecommended,
            } = this.props;
            if (!user && step.nextRequiresAuth()) {
                if (
                    !(await this.showAuthModal({
                        ...this.defaultAuthModalRecommended,
                        ...recommendedAuthModal,
                    }))
                ) {
                    return;
                }

                await this.claimIfUnclaimed();

                history.push(step.nextUrl());
            } else if (!this.state.error) {
                if (user) {
                    await this.claimIfUnclaimed();
                }

                history.push(step.nextUrl());
            }
        } finally {
            this.setState({
                nextInProgress: false,
            });
        }
    };

    save = async (changes: Partial<T>): Promise<FormErrors> => {
        try {
            this.setState({
                saveInProgress: true,
            });

            const res = await this.props.client.mutate<updateBrief, updateBriefVariables>({
                mutation: queries.updateBrief,
                variables: {
                    id: this.props.brief.id,
                    fields: { [this.props.schema]: changes },
                },
            });

            this.form.setData({
                ...this.form.data,
                ...changes,
            });

            // TODO is there a better way to handle these errors? Maybe a view component?
            return this.translateErrors(res.data!.brief.errors);
        } catch (error) {
            this.setState({ error });
            return {};
        } finally {
            this.setState({ saveInProgress: false });
        }
    };

    scrollToFirstError = () => {
        const firstError = this.form.firstError();
        if (!firstError) {
            return;
        }

        this.form.scrollTo(firstError);
    };

    showAuthModal = async (props: AuthFormProps) => {
        if (this.props.viewer.user != null) {
            return true;
        }

        return this.props.login(props);
    };

    launch = async () => {
        const { directAuthModal = this.defaultAuthModalDirect } = this.props;
        let launchTimeout;
        try {
            this.setState({
                nextInProgress: true,
            });

            if (await this.showAuthModal({ ...this.defaultAuthModalDirect, ...directAuthModal })) {
                await this.userInitiatedSave();

                // We've had some reports of launches stalling without an error. This is currently
                // here to give us a picture of how many users are being affected by this.
                launchTimeout = setTimeout(
                    () =>
                        Bugsnag.notify(
                            {
                                name: 'Launch',
                                message: 'Launch timeout',
                            },
                            { metaData: { brief: this.brief.id } }
                        ),
                    15000
                );

                const res = await this.props.client.mutate<launchBrief>({
                    mutation: queries.launchBrief,
                    variables: { id: this.props.brief.id },
                });

                if (res) {
                    this.setState({ launchState: LaunchState.Success });
                }
            }
        } catch (error) {
            Bugsnag.notify(
                { name: 'Launch', message: 'Launch error' },
                { metaData: { brief: this.brief.id, error } }
            );
            this.setState({ launchState: LaunchState.Error });
        } finally {
            if (launchTimeout) {
                clearTimeout(launchTimeout);
            }
            this.setState({ nextInProgress: false });
        }
    };

    translateErrors = (errors: ValidationError[]): FormErrors => {
        const validationErrors: FormErrors = {};

        errors.forEach(({ field, reason }) => {
            validationErrors[field] = (() => {
                switch (reason) {
                    case StructuredBriefErrorReason.TOO_LONG:
                        switch (field) {
                            case 'title':
                                return formatMessage(
                                    __(
                                        'Woah, the name of your project is too long. Please keep it less than {maxSize} characters.'
                                    ),
                                    { maxSize: MAX_TITLE_LENGTH }
                                );
                            default:
                                return __('This doesn’t look quite right. Please try again.');
                        }
                    case StructuredBriefErrorReason.BLANK:
                        switch (field) {
                            case 'title':
                                return __("Don't forget to name your project.");
                            case 'category':
                                return __("Don't forget to choose a category.");
                            case 'customCategory':
                                return __("Don't forget to enter your own category.");
                            case 'description':
                                return __("Don't forget to describe your project.");
                            default:
                                return __('This field is required.');
                        }
                    case StructuredBriefErrorReason.INVALID:
                        return __('This doesn’t look quite right. Please try again.');
                    default:
                        Bugsnag.notify({
                            name: 'MissingValidationError',
                            message: `Missing validation error for "${reason}:${field}"`,
                        });
                        return '';
                }
            })();
        });

        return validationErrors;
    };

    get brief() {
        return this.props.brief;
    }

    get error() {
        if (!this.state.error) {
            return null;
        }

        let message = this.state.error.message;
        if (process.env.NODE_ENV === 'production') {
            message = __('Uh oh... Please try again or refresh the page.');
        }

        return <ErrorBar onDismiss={() => this.clearError()}>{message}</ErrorBar>;
    }

    render() {
        const { step } = this.props;

        const stepFooterProps: StepFooterProps = {
            step,
            isAuthenticated: this.brief.userId > 0,
            nextInProgress: this.state.nextInProgress,
            nextAction: step.hasStepAfter() ? this.handleNextClick : this.launch,
            nextActionLabel: step.nextActionLabel(),
            disabled: this.state.nextInProgress,
            launchAction: this.launch,
        };

        return (
            <BriefContext.Provider value={{ brief: this.brief }}>
                <Page step={step} designers={this.brief.designers}>
                    {this.error}
                    {this.props.children(this.form)}
                    <BriefFooter
                        stepFooterProps={stepFooterProps}
                        launchState={this.state.launchState}
                        brief={this.brief}
                        saving={this.state.nextInProgress || this.state.saveInProgress}
                    />
                </Page>
            </BriefContext.Provider>
        );
    }
}

export default Brief;
